import type { QuestionSetType } from "./QuestionSet";

/** What visitors get on the project when they check in; a subset of the company permissions. */
export type CheckInPermissions = {
  canExecuteAssessments: boolean;
  canExportAssessments: boolean;
  canAccessAllAssessments: boolean;
  canAccessAssessmentHistory: boolean;
  canManageProjects: boolean;
  canManageAssessments: boolean;
  canManageAssessmentsDestructive: boolean;
  accessibleQuestionSetTypes: QuestionSetType[];
};

/**
 * Where a check-in stands: `active` until `expiresAt` (created + grant hours), then `expired`; `closed` when
 * someone explicitly closed (revoked) it, whatever the time.
 */
export type CheckInStatus = "active" | "expired" | "closed";

/** A check-in: visitors scan its rotating QR code to get temporary access to the project. */
export class ProjectCheckIn {
  constructor(
    public readonly uuid: string,
    public readonly projectUuid: string,
    public readonly createdByUuid: string,
    public readonly grantHours: number,
    public readonly permissions: CheckInPermissions,
    public readonly createdAt: Date,
    public readonly expiresAt: Date,
    public readonly closedAt: Date | null,
  ) {}

  get status(): CheckInStatus {
    if (this.closedAt !== null) {
      return "closed";
    }
    return this.expiresAt.getTime() <= Date.now() ? "expired" : "active";
  }

  static fromJson(json: any): ProjectCheckIn {
    return new ProjectCheckIn(
      json["uuid"],
      json["project_uuid"],
      json["created_by_uuid"],
      json["grant_hours"],
      {
        canExecuteAssessments: json["permissions"]["can_execute_assessments"],
        canExportAssessments: json["permissions"]["can_export_assessments"],
        canAccessAllAssessments: json["permissions"]["can_access_all_assessments"],
        canAccessAssessmentHistory: json["permissions"]["can_access_assessment_history"],
        canManageProjects: json["permissions"]["can_manage_projects"],
        canManageAssessments: json["permissions"]["can_manage_assessments"],
        canManageAssessmentsDestructive: json["permissions"]["can_manage_assessments_destructive"],
        accessibleQuestionSetTypes: json["permissions"]["accessible_question_set_types"],
      },
      new Date(json["created_at"]),
      new Date(json["expires_at"]),
      json["closed_at"] ? new Date(json["closed_at"]) : null,
    );
  }
}
